import { Alert, Button, CircularProgress, Container, Grid, Stack, Typography } from "@mui/material";
import Page from "../../minimals/components/page";
import { useSettingsContext } from "../../minimals/components/settings";
import ContactInfo from "./ContactInfo";
import BulletinInfo from "./BulletinInfo";
import { useEffect, useState } from "react";
import produce from "immer";
import {
  BlacklistNameEntity,
  BlacklistNameIndividual,
  BulletinInformation,
  UploadInfo,
  BulletinLocation,
  PersonRecord,
  CompanyRecord
} from "./types";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { stripDataUriPrefix } from "../utils/stripDataUriPrefix";
import { toBase64 } from "../utils/helpers";
import { Contact as OrderEntryContact } from "../orderEntry/types/Contact";

const PERSON_TYPE = 1;
const COMPANY_TYPE = 2;

export default function EditBlacklistName() {
  const { themeStretch } = useSettingsContext();
  const [searchParams] = useSearchParams();
  const entityId = Number(searchParams.get("entityId"));
  const entityType = Number(searchParams.get("entityType"));

  const baseBulletin: BulletinInformation = {
    state: { id: 0, name: "", abbreviation: "", counties: [] },
    county: { id: 0, stateId: 0, name: "", state: { id: 0, name: "", abbreviation: "", counties: [] } },
    note: ""
  };

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isIndividual, setIsIndividual] = useState(true);
  const [individualInfo, setIndividualInfo] = useState<BlacklistNameIndividual>({
    title: "", firstName: "", middleName: "", lastName: "", suffix: "",
    isAttorney: false, taxID: "", isNotary: false, yearAdmittedToBar: "", notes: "", lookupCode: ""
  });
  const [entityInfo, setEntityInfo] = useState<BlacklistNameEntity>({
    name: "", companyTypeId: { value: null, label: "" }, taxID: "", notes: "", lookupCode: ""
  });
  const [bulletinInfo, setBulletinInfo] = useState<BulletinInformation>(baseBulletin);
  const [uploads, setUploads] = useState<UploadInfo[]>([]);
  const [existingBulletins, setExistingBulletins] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [bulletinStateError, setBulletinStateError] = useState(false);
  const [bulletinError, setBulletinError] = useState(false);
  const [notes, setNotes] = useState("");
  const [locations, setLocations] = useState<Partial<BulletinLocation>[]>([{ stateId: 0 }]);

  useEffect(() => {
    if (!entityId || !entityType) { setNotFound(true); setLoading(false); return; }
    const fetch = async () => {
      try {
        if (entityType === PERSON_TYPE) {
          const { data } = await axios.get<PersonRecord>(`/api/blacklist/GetPersonRecordById?id=${entityId}`);
          if (!data) { setNotFound(true); return; }
          setIsIndividual(true);
          setIndividualInfo({
            title: data.title || "",
            firstName: data.firstName || "",
            middleName: data.middleName || "",
            lastName: data.lastName || "",
            suffix: data.suffix || "",
            isAttorney: data.isAttorney || false,
            taxID: data.taxID || "",
            isNotary: data.isNotary || false,
            yearAdmittedToBar: data.yearAdmittedToBar || "",
            notes: "",
            lookupCode: ""
          });
          setNotes(data.notes || "");
          setExistingBulletins(data.bulletins?.map((b: any) => b.bulletin) || []);
        } else if (entityType === COMPANY_TYPE) {
          const { data } = await axios.get<CompanyRecord>(`/api/blacklist/getCompanyRecordById?id=${entityId}`);
          if (!data) { setNotFound(true); return; }
          setIsIndividual(false);
          setEntityInfo({
            name: data.name || "",
            companyTypeId: data.companyType
              ? { value: data.companyTypeId ?? null, label: data.companyType.name }
              : { value: null, label: "" },
            taxID: data.taxID || "",
            notes: "",
            lookupCode: ""
          });
          setNotes(data.notes || "");
          setExistingBulletins(data.bulletins?.map((b: any) => b.bulletin) || []);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [entityId, entityType]);

  const addOrEditUpload = (newUpload: UploadInfo, index?: number) => {
    if (index === undefined) {
      setUploads((prev) => [...prev, newUpload]);
      return;
    }
    setUploads((prev) => prev.map((u, i) => (i === index ? newUpload : u)));
  };

  const changeIndividualInfo = (label: keyof BlacklistNameIndividual, value: any) => {
    setIndividualInfo(produce(individualInfo, (draft: any) => { draft[label] = value; }));
  };

  const changeEntityInfo = (label: keyof BlacklistNameEntity, value: any) => {
    setEntityInfo(produce(entityInfo, (draft: any) => { draft[label] = value; }));
  };

  const changeBulletinInfo = (updates: Partial<BulletinInformation>) => {
    setBulletinInfo(produce(bulletinInfo, (draft: any) => {
      for (const [key, value] of Object.entries(updates)) { draft[key] = value; }
    }));
  };

  const handleRemoveUpload = (index: number) => {
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const onBlur = () => {
    if (locations.some((loc) => loc.stateId && loc.stateId > 0)) setBulletinStateError(false);
  };

  const onSubmit = async () => {
    if (uploads.length > 0 && !locations.some((loc) => loc.stateId && loc.stateId > 0)) {
      setBulletinStateError(true);
      return;
    }

    const validLocations = locations
      .filter((loc) => loc.stateId && loc.stateId > 0)
      .map((loc) => ({ stateId: loc.stateId, countyId: loc.countyId }));

    const bulletinsWithBase64 = await Promise.all(
      uploads.map(async (u) => ({
        bulletin: {
          number: u.number,
          date: u.date,
          //@ts-ignore
          underwriterId: u.underwriter.id
        },
        base64File: stripDataUriPrefix(await toBase64(u.file))
      }))
    );

    setSubmitting(true);
    try {
      if (entityType === PERSON_TYPE) {
        await axios.post(`/api/blacklist/appendToPersonRecord?entityId=${entityId}`, {
          person: { ...individualInfo, notes },
          locations: validLocations,
          bulletinsWithBase64
        });
      } else {
        await axios.post(`/api/blacklist/appendToCompanyRecord?entityId=${entityId}`, {
          company: {
            ...entityInfo,
            notes,
            //@ts-ignore
            companyTypeId: entityInfo?.companyTypeId?.id || null
          },
          locations: validLocations,
          bulletinsWithBase64
        });
      }
      setSuccessMessage(true);
      setSubmitting(false);
    } catch {
      setSubmitting(false);
    }
  };

  const setBlacklistContactFromPhonebookContact = (contact: OrderEntryContact, isInd: boolean) => {
    if (isInd) {
      setIndividualInfo((prev) => ({
        ...prev,
        title: contact.title || "",
        firstName: contact.firstName || "",
        middleName: contact.middleName || "",
        lastName: contact.lastName || "",
        suffix: contact.suffix || "",
        lookupCode: contact.lookupCode || ""
      }));
    } else {
      setEntityInfo((prev) => ({ ...prev, name: contact.name || "", lookupCode: contact.lookupCode || "" }));
    }
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (notFound) return <Typography sx={{ m: 4 }}>Record not found.</Typography>;

  return (
    <Page title="Blacklist" width="100%">
      <Container maxWidth={themeStretch ? false : "xl"}>
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h4" marginBottom={2}>
                Edit Blacklist Name
              </Typography>
            </Stack>
            <ContactInfo
              individualInfo={individualInfo}
              setIndividualInfo={changeIndividualInfo}
              entityInfo={entityInfo}
              changeEntityInfo={changeEntityInfo}
              isIndividual={isIndividual}
              setIsIndividual={setIsIndividual}
              nameError={false}
              companyError={false}
              onBlurIndividual={onBlur}
              onBlurEntity={onBlur}
              setBlacklistContactFromPhonebookContact={setBlacklistContactFromPhonebookContact}
              isEditMode
            />
            <BulletinInfo
              bulletinInfo={bulletinInfo}
              setBulletinInfo={changeBulletinInfo}
              addUpload={addOrEditUpload}
              uploads={uploads}
              handleRemoveUpload={handleRemoveUpload}
              bulletinStateError={bulletinStateError}
              notes={notes}
              setNotes={setNotes}
              onBlurIndividual={onBlur}
              bulletinError={bulletinError}
              setBulletinError={setBulletinError}
              locations={locations}
              setLocations={setLocations}
              isEditMode
              existingBulletins={existingBulletins}
            />
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="right" spacing={1.5} width={784} marginTop={8}>
          <Button variant="outlined" color="inherit" onClick={() => window.close()}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
          {successMessage && (
            <Alert sx={{ marginTop: 2 }} severity="success">
              Successfully Saved
            </Alert>
          )}
        </Stack>
      </Container>
    </Page>
  );
}
